import React, { useState, useEffect } from 'react';
import { 
  Mail, MessageSquare, Save, Eye, X, 
  ChevronDown, ChevronRight, Sparkles,
  FileText, Bell, CreditCard, Calendar,
  AlertTriangle, Check, Copy, Info
} from 'lucide-react';

// Available template variables
const templateVariables = {
  customer: [
    { key: '{{customer_name}}', label: 'Customer Name', example: 'John Smith' },
    { key: '{{customer_first_name}}', label: 'First Name', example: 'John' },
    { key: '{{customer_email}}', label: 'Email', example: 'john@email.com' },
    { key: '{{customer_phone}}', label: 'Phone', example: '07700 900123' },
    { key: '{{customer_address}}', label: 'Address', example: '45 High Street, Brighton' },
  ],
  invoice: [
    { key: '{{invoice_number}}', label: 'Invoice Number', example: 'INV-2026-0001' },
    { key: '{{invoice_amount}}', label: 'Invoice Amount', example: '£96.00' },
    { key: '{{invoice_date}}', label: 'Invoice Date', example: '03/02/2026' },
    { key: '{{due_date}}', label: 'Due Date', example: '17/02/2026' },
    { key: '{{invoice_link}}', label: 'Invoice Link', example: 'https://...' },
    { key: '{{payment_link}}', label: 'Payment Link', example: 'https://...' },
  ],
  job: [
    { key: '{{job_date}}', label: 'Job Date', example: '03/02/2026' },
    { key: '{{job_time}}', label: 'Job Time', example: '10:00 AM' },
    { key: '{{job_service}}', label: 'Service Type', example: 'Window Clean' },
    { key: '{{job_price}}', label: 'Job Price', example: '£35.00' },
    { key: '{{worker_name}}', label: 'Worker Name', example: 'Dan' },
  ],
  business: [
    { key: '{{business_name}}', label: 'Business Name', example: 'StreakFree' },
    { key: '{{business_phone}}', label: 'Business Phone', example: '01onal 123456' },
    { key: '{{business_email}}', label: 'Business Email', example: 'hello@perfectpanes.co.uk' },
  ],
};

// Default templates
const defaultTemplates = {
  email: {
    invoice: {
      name: 'Invoice Notification',
      subject: 'Invoice {{invoice_number}} from {{business_name}}',
      body: `Hi {{customer_first_name}},

Please find your latest invoice attached.

Invoice: {{invoice_number}}
Amount: {{invoice_amount}}
Due Date: {{due_date}}

You can view and pay your invoice here:
{{invoice_link}}

Or pay directly by card:
{{payment_link}}

Thank you for choosing {{business_name}}.

Best regards,
{{business_name}}`,
    },
    reminder: {
      name: 'Payment Reminder',
      subject: 'Reminder: Invoice {{invoice_number}} is due',
      body: `Hi {{customer_first_name}},

This is a friendly reminder that invoice {{invoice_number}} for {{invoice_amount}} is due on {{due_date}}.

Pay now: {{payment_link}}

If you've already paid, please disregard this message.

Thanks,
{{business_name}}`,
    },
    confirmation: {
      name: 'Job Completion',
      subject: 'Your {{job_service}} is complete!',
      body: `Hi {{customer_first_name}},

Great news! Your {{job_service}} has been completed today.

We hope you're happy with the service. If you have any questions, please don't hesitate to contact us.

Your invoice will be sent shortly.

Thank you for your business!
{{business_name}}`,
    },
    appointment: {
      name: 'Appointment Reminder',
      subject: 'Reminder: {{job_service}} tomorrow',
      body: `Hi {{customer_first_name}},

Just a reminder that we'll be visiting tomorrow for your {{job_service}}.

Date: {{job_date}}
Time: {{job_time}}
Service: {{job_service}}

If you need to reschedule, please let us know as soon as possible.

See you tomorrow!
{{business_name}}`,
    },
  },
  sms: {
    reminder: {
      name: 'Job Reminder',
      body: `Hi {{customer_first_name}}, reminder: {{job_service}} tomorrow at {{job_time}}. {{business_name}}`,
      charLimit: 160,
    },
    onMyWay: {
      name: 'On My Way',
      body: `Hi {{customer_first_name}}, {{worker_name}} from {{business_name}} is on the way for your {{job_service}}. ETA ~15 mins.`,
      charLimit: 160,
    },
    complete: {
      name: 'Job Complete',
      body: `Hi {{customer_first_name}}, your {{job_service}} is done! Invoice to follow. Thanks - {{business_name}}`,
      charLimit: 160,
    },
    payment: {
      name: 'Payment Request',
      body: `Hi {{customer_first_name}}, invoice {{invoice_number}} ({{invoice_amount}}) is ready. Pay here: {{payment_link}} - {{business_name}}`,
      charLimit: 160,
    },
  },
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TemplateEditor = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('email');
  const [selectedTemplate, setSelectedTemplate] = useState('invoice');
  const [templates, setTemplates] = useState(defaultTemplates);
  const [showPreview, setShowPreview] = useState(false);
  const [showVariables, setShowVariables] = useState(true);
  const [savedNotification, setSavedNotification] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('StreakFree');

  // Load company settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) return;
        
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.company_logo) setCompanyLogo(data.company_logo);
          if (data.company_name) setCompanyName(data.company_name);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  const currentTemplate = activeTab === 'email' 
    ? templates.email[selectedTemplate] 
    : templates.sms[selectedTemplate];

  const updateTemplate = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [selectedTemplate]: {
          ...prev[activeTab][selectedTemplate],
          [field]: value
        }
      }
    }));
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = currentTemplate.body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      updateTemplate('body', newText);
      
      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
  };

  const handleSave = () => {
    setSavedNotification(true);
    setTimeout(() => setSavedNotification(false), 2000);
  };

  const replaceVariables = (text) => {
    let result = text;
    Object.values(templateVariables).flat().forEach(v => {
      result = result.replace(new RegExp(v.key.replace(/[{}]/g, '\\$&'), 'g'), v.example);
    });
    return result;
  };

  const smsCharCount = activeTab === 'sms' ? currentTemplate.body.length : 0;
  const smsSegments = Math.ceil(smsCharCount / 160);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="template-editor-modal">
      <div className="bg-white rounded-2xl max-w-5xl w-full modal-shell-h overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              {activeTab === 'email' ? <Mail size={20} className="text-emerald-600" /> : <MessageSquare size={20} className="text-emerald-600" />}
            </div>
            <div>
              <h2 className="font-bold text-zinc-900">Message Templates</h2>
              <p className="text-sm text-zinc-500">Customize your email and SMS notifications</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors">
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200">
          <button
            onClick={() => { setActiveTab('email'); setSelectedTemplate('invoice'); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium transition-colors ${
              activeTab === 'email' 
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-blue-50/50' 
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Mail size={18} />
            Email Templates
          </button>
          <button
            onClick={() => { setActiveTab('sms'); setSelectedTemplate('reminder'); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium transition-colors ${
              activeTab === 'sms' 
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-blue-50/50' 
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <MessageSquare size={18} />
            SMS Templates
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Template List */}
          <div className="w-64 border-r border-zinc-200 overflow-y-auto bg-zinc-50">
            <div className="p-3">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Templates</p>
              {Object.entries(activeTab === 'email' ? templates.email : templates.sms).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTemplate(key)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors mb-1 ${
                    selectedTemplate === key 
                      ? 'bg-blue-100 text-emerald-700' 
                      : 'hover:bg-white text-zinc-700'
                  }`}
                >
                  {key === 'invoice' && <FileText size={16} />}
                  {key === 'reminder' && <Bell size={16} />}
                  {key === 'confirmation' && <Check size={16} />}
                  {key === 'appointment' && <Calendar size={16} />}
                  {key === 'onMyWay' && <Calendar size={16} />}
                  {key === 'complete' && <Check size={16} />}
                  {key === 'payment' && <CreditCard size={16} />}
                  <span className="text-sm font-medium">{template.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Subject (Email only) */}
              {activeTab === 'email' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Subject Line</label>
                  <input
                    type="text"
                    value={currentTemplate.subject}
                    onChange={(e) => updateTemplate('subject', e.target.value)}
                    className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter email subject..."
                    data-testid="email-subject-input"
                  />
                </div>
              )}

              {/* Body */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-zinc-700">Message Body</label>
                  {activeTab === 'sms' && (
                    <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                      smsCharCount > 160 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                    }`}>
                      {smsCharCount}/160 chars · {smsSegments} segment{smsSegments !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
                <textarea
                  id="template-body"
                  value={currentTemplate.body}
                  onChange={(e) => updateTemplate('body', e.target.value)}
                  rows={activeTab === 'email' ? 12 : 4}
                  className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono text-sm resize-none"
                  placeholder="Enter message content..."
                  data-testid="template-body-input"
                />
                {activeTab === 'sms' && smsCharCount > 160 && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Message exceeds 160 characters and will be sent as {smsSegments} SMS segments
                  </p>
                )}
              </div>

              {/* Variables */}
              <div className="border border-zinc-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="w-full flex items-center justify-between p-4 bg-zinc-50 hover:bg-zinc-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-emerald-600" />
                    <span className="font-medium text-zinc-700">Template Variables</span>
                  </div>
                  {showVariables ? <ChevronDown size={18} className="text-zinc-400" /> : <ChevronRight size={18} className="text-zinc-400" />}
                </button>
                
                {showVariables && (
                  <div className="p-4 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(templateVariables).map(([category, variables]) => (
                      <div key={category}>
                        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">{category}</p>
                        <div className="space-y-1">
                          {variables.map(v => (
                            <button
                              key={v.key}
                              onClick={() => insertVariable(v.key)}
                              className="w-full flex items-center justify-between p-2 text-left text-sm bg-zinc-50 hover:bg-blue-50 rounded-lg transition-colors group"
                              title={`Example: ${v.example}`}
                            >
                              <span className="text-zinc-600 group-hover:text-emerald-600">{v.label}</span>
                              <Copy size={12} className="text-zinc-400 opacity-0 group-hover:opacity-100" />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:text-zinc-900 hover:bg-white rounded-lg transition-colors"
              >
                <Eye size={18} />
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
              
              <div className="flex items-center gap-3">
                {savedNotification && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <Check size={16} />
                    Saved!
                  </span>
                )}
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
                  data-testid="save-template-btn"
                >
                  <Save size={18} />
                  Save Template
                </button>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="w-80 border-l border-zinc-200 overflow-y-auto bg-zinc-50 p-4">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Preview</p>
              
              {activeTab === 'email' ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Email Header with Logo */}
                  <div className="bg-zinc-800 p-4 text-center">
                    {companyLogo ? (
                      <img 
                        src={companyLogo} 
                        alt={companyName}
                        className="w-12 h-12 rounded-lg object-cover mx-auto"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center mx-auto">
                        <span className="text-white font-bold text-lg">
                          {companyName.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <p className="text-white font-medium text-sm mt-2">{companyName}</p>
                  </div>
                  
                  <div className="p-3 border-b border-zinc-100 bg-zinc-50">
                    <p className="text-xs text-zinc-400 mb-1">Subject</p>
                    <p className="text-sm font-medium text-zinc-900">{replaceVariables(currentTemplate.subject)}</p>
                  </div>
                  <div className="p-4">
                    <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans">
                      {replaceVariables(currentTemplate.body)}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-900 rounded-2xl p-4">
                  <div className="bg-emerald-500 text-white p-3 rounded-xl text-sm max-w-[85%] ml-auto">
                    {replaceVariables(currentTemplate.body)}
                  </div>
                  <p className="text-xs text-zinc-500 text-right mt-2">Just now</p>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-2">
                  <Info size={14} className="text-emerald-600 mt-0.5" />
                  <p className="text-xs text-emerald-700">
                    Variables in {'{{brackets}}'} will be replaced with actual customer and job data when sent.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
